// hooks/useBreakpoint.ts
import { useState, useEffect, useMemo, useRef } from 'react'
import { Breakpoint } from 'shared/types/Breakpoint'
import { BreakpointWidthState } from 'shared/types/BreakpointWidthState'
import { UseBreakpointOptions } from 'shared/types/UseBreakpointOptions'

import {
    debounce,
    getBreakpoint,
    isMobile,
    isTablet,
    isDesktop,
    getOrientation,
    mergeBreakpoints,
} from 'shared/lib/breakpointUtils'


export const useBreakpoint = (options: UseBreakpointOptions = {}): BreakpointWidthState => {
    const {
        breakpoints: customBreakpoints,
        debounceDelay = 100,
        passive = true,
    } = options

    // Merge custom breakpoints with defaults
    const breakpoints = useMemo(() =>
        mergeBreakpoints(customBreakpoints),
        [customBreakpoints]
    )

    // Initial state based on current window dimensions
    const [state, setState] = useState<BreakpointWidthState>(() => {
        const width = typeof window !== 'undefined' ? window.innerWidth : 0
        const height = typeof window !== 'undefined' ? window.innerHeight : 0
        const breakpoint = getBreakpoint(width, breakpoints)

        return {
            breakpoint,
            isMobile: isMobile(breakpoint),
            isTablet: isTablet(breakpoint),
            isDesktop: isDesktop(breakpoint),
            width,
            height,
            orientation: getOrientation(width, height),
        }
    })

    const stateRef = useRef(state)
    stateRef.current = state

    useEffect(() => {
        if (typeof window === 'undefined') {
            return
        }

        const updateBreakpoint = () => {
            const width = window.innerWidth
            const height = window.innerHeight
            const breakpoint = getBreakpoint(width, breakpoints)
            const orientation = getOrientation(width, height)

            // Only update state if something actually changed
            if (
                breakpoint !== stateRef.current.breakpoint ||
                width !== stateRef.current.width ||
                height !== stateRef.current.height ||
                orientation !== stateRef.current.orientation
            ) {
                setState({
                    breakpoint,
                    isMobile: isMobile(breakpoint),
                    isTablet: isTablet(breakpoint),
                    isDesktop: isDesktop(breakpoint),
                    width,
                    height,
                    orientation,
                })
            }
        }

        const debouncedUpdate = debounceDelay > 0
            ? debounce(updateBreakpoint, debounceDelay)
            : updateBreakpoint

        updateBreakpoint()

        const eventListenerOptions: AddEventListenerOptions | boolean = passive
            ? { passive: true }
            : false

        window.addEventListener('resize', debouncedUpdate, eventListenerOptions)
        window.addEventListener('orientationchange', debouncedUpdate, eventListenerOptions)

        return () => {
            window.removeEventListener('resize', debouncedUpdate, eventListenerOptions as EventListenerOptions)
            window.removeEventListener('orientationchange', debouncedUpdate, eventListenerOptions as EventListenerOptions)
        }
    }, [breakpoints, debounceDelay, passive])

    return state
}